import {
  LAUNCH_BLOCKER_COPY,
  POLICY_REVIEW_POLL_MS,
  POLICY_REVIEW_WAIT_MS,
  type StudyStepSlug,
  studyStep,
} from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type LaunchBlocker,
  type LaunchCheck,
  fetchLaunchCheck,
  fetchPolicyReview,
  requestPolicyReview,
} from '../api/client.ts';
import { isApiError } from '../api/errors.ts';
import { RouteLink } from '../studies/RouteLink.tsx';
import { type Route, routeToPath } from '../studies/routing.ts';
import { useRequestWait } from '../studies/use-request-wait.ts';

export interface LaunchCheckState {
  /** Every blocker the last list read named, or `null` until a read has answered. */
  blockers: readonly LaunchBlocker[] | null;
  /**
   * The latest read or request failed, so what stands in the way now is not known; `blockers` are
   * still the last list read, shown beside the failure.
   */
  failed: boolean;
  /** A pending review outlasted the wait, and "Check again" asks for it again. */
  waitedOut: boolean;
  /** "Check again" is out. */
  checking: boolean;
  checkAgain: () => Promise<void>;
}

/**
 * The Launch step's blocker list, and the review it asks for.
 *
 * Two things ask for the policy review, and nothing else does: Launch, open on a ready study whose
 * list says a review is required, and "Check again". The first asks once for each set of versions
 * the study holds (the check's `reviewSubject`), whether the study arrived ready, became ready while
 * the step was open, or held a review that went past its wait — and once more for a set whose review
 * it saw pending and then past its wait; it never asks over a verdict, and a review that could not
 * be had, or a request that failed, is asked again only by "Check again". A review it finds pending is
 * re-read every `POLICY_REVIEW_POLL_MS` until it settles, when the list is read again, or until
 * `POLICY_REVIEW_WAIT_MS` passes, when "Check again" is offered. "Check again" asks with `retry`,
 * which the server honours only over a review that could not be had or read: a refusal is final for
 * the study as it stands, and what changes it is an edit on the step it names.
 *
 * The list is re-read whenever the study is (`refreshToken`) and whenever the panel asks
 * (`recheckToken`). Every read and request takes a turn, and an answer — or a failure — overtaken
 * by a later one sets nothing.
 *
 * `active` is false where the panel has moved past paying — set up, or launched — and the list
 * then holds nothing.
 */
export const useLaunchCheck = ({
  studyId,
  active,
  ready,
  refreshToken,
  recheckToken,
}: {
  studyId: string;
  active: boolean;
  ready: boolean;
  refreshToken: number;
  recheckToken: number;
}): LaunchCheckState => {
  const [check, setCheck] = useState<{ studyId: string; check: LaunchCheck } | null>(null);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);
  /** The sets of versions this panel has asked a review of, so each is asked once. */
  const asked = useRef(new Set<string>());
  /** The sets of versions whose review this panel has seen pending. */
  const seenPending = useRef(new Set<string>());
  /** The sets of versions asked again after a pending review outlasted its wait. */
  const askedAgain = useRef(new Set<string>());
  const alive = useRef(true);
  /** Each read's turn, so an answer overtaken by a later read sets nothing. */
  const latest = useRef(0);

  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const turn = latest.current + 1;
    latest.current = turn;

    try {
      const loaded = await fetchLaunchCheck(studyId);
      if (alive.current && latest.current === turn) {
        setCheck({ studyId, check: loaded });
        setFailed(false);
      }
    } catch {
      if (alive.current && latest.current === turn) {
        // Unknown is not clear: the last list stays, and Pay stays withheld until a read answers.
        setFailed(true);
      }
    }
  }, [studyId]);

  /**
   * Asks for the review, and then reads the list. A refusal of the request is what the list says
   * too — a study that is not ready, or a Goal that is not chosen — so it is read rather than
   * reported; anything else is the list failing.
   */
  const ask = useCallback(
    async (retry: boolean) => {
      const turn = latest.current + 1;
      latest.current = turn;

      try {
        await requestPolicyReview(studyId, { retry });
      } catch (error) {
        if (!isApiError(error) || error.status >= 500) {
          if (alive.current && latest.current === turn) {
            setFailed(true);
          }

          return;
        }
      }

      await load();
    },
    [studyId, load],
  );

  useEffect(() => {
    if (!active) {
      setCheck(null);
      setFailed(false);

      return;
    }

    void load();
  }, [studyId, active, ready, refreshToken, recheckToken, load]);

  const current = active && check?.studyId === studyId ? check.check : null;
  const subject = current?.reviewSubject ?? null;
  const required =
    current?.blockers.some((blocker) => blocker.kind === 'policy_review_required') === true;
  const review = current?.policyReview ?? null;
  const reviewOutcome = review?.outcome ?? null;

  useEffect(() => {
    if (subject !== null && reviewOutcome === 'pending') {
      seenPending.current.add(subject);
    }
  }, [subject, reviewOutcome]);

  useEffect(() => {
    if (!ready || !required || subject === null) {
      return;
    }

    if (!asked.current.has(subject)) {
      asked.current.add(subject);
      void ask(false);

      return;
    }

    // A review asked for these versions that went pending and then outlasted its wait reads as
    // required: it is asked once more. A request that failed never went pending, so it waits for
    // "Check again".
    if (seenPending.current.has(subject) && !askedAgain.current.has(subject)) {
      askedAgain.current.add(subject);
      void ask(false);
    }
  }, [ready, required, subject, ask]);

  const pendingId =
    review?.outcome === 'pending' ? (review.requestedAt ?? `${studyId}-pending`) : null;

  const pollReview = useCallback(async () => {
    try {
      const view = await fetchPolicyReview(studyId);
      if (view.outcome !== 'pending') {
        await load();
      }
    } catch {
      // A failed poll is the next poll's to retry, within the same wait.
    }
  }, [studyId, load]);

  const waitedOut = useRequestWait({
    pendingId,
    pollMs: POLICY_REVIEW_POLL_MS,
    waitMs: POLICY_REVIEW_WAIT_MS,
    load: pollReview,
    alive,
  });

  const checkAgain = useCallback(async () => {
    setChecking(true);
    try {
      if (ready) {
        await ask(true);
      } else {
        await load();
      }
    } finally {
      if (alive.current) {
        setChecking(false);
      }
    }
  }, [ready, ask, load]);

  return { blockers: current?.blockers ?? null, failed, waitedOut, checking, checkAgain };
};

export interface LaunchBlockersProps {
  studyId: string;
  state: LaunchCheckState;
  /** Opens a step of this study; absent, a blocker's link loads its page. */
  onOpenStep?: (stepSlug: StudyStepSlug) => void;
}

/**
 * The list itself: a heading, each blocker with its step's link, and "Check again" where checking
 * again can change something — a review that could not be had, one that outlasted its wait, a panel
 * that could not be asked, or a list that could not be read, whose failure is said above the last
 * list read. Renders nothing for a study with none.
 */
export const LaunchBlockers = ({ studyId, state, onOpenStep }: LaunchBlockersProps) => {
  const { blockers, failed, waitedOut, checking, checkAgain } = state;

  const navigate = (route: Route) => {
    if (route.name === 'study-step' && onOpenStep !== undefined) {
      onOpenStep(route.stepSlug);
    } else {
      window.location.assign(routeToPath(route));
    }
  };

  const retryButton = (
    <button
      data-test-id="launch-review-retry"
      disabled={checking}
      onClick={() => void checkAgain()}
      type="button"
    >
      {checking ? LAUNCH_BLOCKER_COPY.checkingAgain : LAUNCH_BLOCKER_COPY.checkAgain}
    </button>
  );

  const failure = (
    <p className="chat-error" data-test-id="launch-blockers-failed" role="alert">
      {LAUNCH_BLOCKER_COPY.loadFailed}
    </p>
  );

  if (failed && (blockers === null || blockers.length === 0)) {
    return (
      <div className="launch-blockers" data-test-id="launch-blockers">
        {failure}
        {retryButton}
      </div>
    );
  }

  if (blockers === null) {
    return (
      <p className="launch-blockers-loading" data-test-id="launch-blockers-loading" role="status">
        {LAUNCH_BLOCKER_COPY.loading}
      </p>
    );
  }

  if (blockers.length === 0) {
    return null;
  }

  const overdue = waitedOut && blockers.some((blocker) => blocker.kind === 'policy_review_pending');
  const offerRetry = failed || overdue || blockers.some((blocker) => blocker.action === 'retry');

  return (
    <div className="launch-blockers" data-test-id="launch-blockers">
      <h3 className="launch-blockers-heading">{LAUNCH_BLOCKER_COPY.heading}</h3>
      {failed && failure}

      <ul className="launch-blockers-list" aria-live="polite">
        {blockers.map((blocker, index) => (
          <li
            className="launch-blocker"
            data-test-id={`launch-blocker-${blocker.kind}`}
            // A study can hold two blockers of one kind at two steps, and two refusals at one.
            key={`${blocker.kind}-${blocker.step ?? 'none'}-${String(index)}`}
          >
            <span>
              {overdue && blocker.kind === 'policy_review_pending'
                ? LAUNCH_BLOCKER_COPY.reviewOverdue
                : blocker.message}
            </span>
            {blocker.step !== null && (
              <RouteLink
                to={{ name: 'study-step', studyId, stepSlug: blocker.step }}
                onNavigate={navigate}
                testId={`launch-blocker-open-${blocker.step}`}
              >
                {LAUNCH_BLOCKER_COPY.openStep(studyStep(blocker.step).name)}
              </RouteLink>
            )}
          </li>
        ))}
      </ul>

      {offerRetry && retryButton}
    </div>
  );
};
